import axios from 'axios';
import authService from '../api/authService';

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5177/api";

interface DataProviderType {
    getList: (params: GetListParams) => Promise<{ data: any[]; total: number }>;
    getOne: (params: GetOneParams) => Promise<{ data: any }>;
    create: (params: CreateParams) => Promise<{ data: any }>;
    update: (params: UpdateParams) => Promise<{ data: any }>;
    deleteOne: (params: DeleteOneParams) => Promise<{ data: any }>;
    getApiUrl: () => string;
}

interface GetListParams {
    resource: string;
    pagination?: {
        current?: number;
        pageSize?: number;
    };
}

interface GetOneParams {
    resource: string;
    id: string;
}

interface CreateParams {
    resource: string;
    variables: Record<string, any>;
}

interface UpdateParams {
    resource: string;
    id: string;
    variables: Record<string, any>;
}

interface DeleteOneParams {
    resource: string;
    id: string;
}

const handleResponse = async (response: Response) => {
    if (!response.ok) {
        const error = await response.text().catch(() => 'Unknown error');
        if (response.status === 403) {
            throw new Error('You do not have permission to perform this action.');
        }
        throw new Error(`API error: ${error}`);
    }
    return response.json();
};

export const customDataProvider: Partial<DataProviderType> = {
    getList: async ({ resource, pagination }: GetListParams) => {
        const current = pagination?.current || 1;
        const pageSize = pagination?.pageSize || 10;
        const start = (current - 1) * pageSize;

        if (resource === "users") {
            try {
                const response = await axios.get(`${API_URL}/auth/users`, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    },
                    params: {
                        skip: start,
                        take: pageSize
                    }
                });

                return {
                    data: response.data,
                    total: response.data.length // This should ideally come from a count endpoint
                };
            } catch (error) {
                console.error('Error fetching users:', error);
                return { data: [], total: 0 };
            }
        }

        if (resource === "customers") {
            try {
                const response = await axios.get(`${API_URL}/Customers`, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    },
                    params: {
                        skip: start,
                        take: pageSize
                    }
                });

                return {
                    data: response.data,
                    total: response.data.length // This should ideally come from a count endpoint
                };
            } catch (error) {
                console.error('Error fetching customers:', error);
                return { data: [], total: 0 };
            }
        }

        // Handle other resources
        return { data: [], total: 0 };
    },

    getOne: async ({ resource, id }: GetOneParams) => {
        if (resource === "users") {
            try {
                const response = await axios.get(`${API_URL}/auth/users/${id}`, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: response.data };
            } catch (error) {
                console.error(`Error fetching user with id ${id}:`, error);
                throw new Error(`Could not fetch user with id ${id}`);
            }
        }

        if (resource === "customers") {
            try {
                const response = await axios.get(`${API_URL}/Customers/${id}`, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: response.data };
            } catch (error) {
                console.error(`Error fetching customer with id ${id}:`, error);
                throw new Error(`Could not fetch customer with id ${id}`);
            }
        }

        // Handle other resources
        throw new Error(`Resource ${resource} not supported`);
    },

    create: async ({ resource, variables }: CreateParams) => {
        if (resource === "users") {
            try {
                const response = await axios.post(`${API_URL}/auth/users`, variables, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: response.data };
            } catch (error) {
                console.error('Error creating user:', error);
                throw new Error('Could not create user');
            }
        }

        if (resource === "customers") {
            try {
                const response = await axios.post(`${API_URL}/Customers`, variables, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: response.data };
            } catch (error) {
                console.error('Error creating customer:', error);
                throw new Error('Could not create customer');
            }
        }

        // Handle other resources
        throw new Error(`Resource ${resource} not supported`);
    },

    update: async ({ resource, id, variables }: UpdateParams) => {
        if (resource === "users") {
            try {
                const response = await axios.put(`${API_URL}/auth/users/${id}`, variables, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: response.data || variables };
            } catch (error) {
                console.error(`Error updating user with id ${id}:`, error);
                throw new Error(`Could not update user with id ${id}`);
            }
        }

        if (resource === "customers") {
            try {
                const response = await axios.put(`${API_URL}/Customers/${id}`, variables, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: response.data || variables };
            } catch (error) {
                console.error(`Error updating customer with id ${id}:`, error);
                throw new Error(`Could not update customer with id ${id}`);
            }
        }

        // Handle other resources
        throw new Error(`Resource ${resource} not supported`);
    },

    deleteOne: async ({ resource, id }: DeleteOneParams) => {
        if (resource === "users") {
            try {
                await axios.delete(`${API_URL}/auth/users/${id}`, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: { id } };
            } catch (error) {
                console.error(`Error deleting user with id ${id}:`, error);
                throw new Error(`Could not delete user with id ${id}`);
            }
        }

        if (resource === "customers") {
            try {
                await axios.delete(`${API_URL}/Customers/${id}`, {
                    headers: {
                        Authorization: `Bearer ${authService.getAccessToken()}`
                    }
                });

                return { data: { id } };
            } catch (error) {
                console.error(`Error deleting customer with id ${id}:`, error);
                throw new Error(`Could not delete customer with id ${id}`);
            }
        }

        // Handle other resources
        throw new Error(`Resource ${resource} not supported`);
    },

    getApiUrl: () => API_URL
}; 
